import { isEqual, mergeWith } from 'lodash-es';
import React, { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Content } from 'src/contexts/ContentContext';
import { SimpleTable } from 'src/core/ui';
import { useEventMarketplaceSettings } from 'src/hooks/useEventMarketplaceSettings';
import { MultiDetailSection } from 'src/modals/common';
import { ContentId } from 'src/utils/constants/contentId';
import { compareMarketplace } from 'src/utils/eventWithDataUtils';
import { isMarketplaceActive } from 'src/utils/userUtils';
import {
  Event,
  ListingMarketplaceSetting,
  Marketplace,
  MergeListingGroupInput,
} from 'src/WebApiController';

import { MergeListingGroupInputListFields } from '../groupingTypes';
import { MarketplaceDisplay } from './MarketplaceDisplay';
import * as styles from './MarketplaceDisplay.css';

/**
 * Get maerketplace id from marketplace enum.
 * This assumes the enum order is aligned with the Marketplace.Marketplace table
 */
const getMarketplaceIdFromMarketplace = (marketplace: Marketplace) => {
  let marketplaceId = 0;
  Object.values(Marketplace).forEach((mp, index) => {
    if (mp === marketplace) {
      marketplaceId = index + 1;
    }
  });
  return marketplaceId;
};

/**
 * Merge listing marketplace settings from two groups
 */
const mergeListingMarketplaceSettings = (
  marketplace1: { [key: string]: ListingMarketplaceSetting },
  marketplace2: { [key: string]: ListingMarketplaceSetting }
) => {
  return mergeWith(
    { ...marketplace1 },
    marketplace2,
    (
      setting1: ListingMarketplaceSetting,
      setting2: ListingMarketplaceSetting
    ) => {
      if (
        !isEqual(
          setting1?.listingBroadcastStateId,
          setting2?.listingBroadcastStateId
        )
      ) {
        return {
          listingBroadcastStateId: Math.max(
            setting1?.listingBroadcastStateId ?? 1,
            setting2?.listingBroadcastStateId ?? 1
          ),
        };
      }
      return setting1 ?? setting2;
    }
  );
};

type MarketplaceBroadcastProps = {
  event?: Event;
};

export const ListingGroupMarketplaceBroadcast: React.FC<
  MarketplaceBroadcastProps
> = ({ event }) => {
  const { watch, setValue } =
    useFormContext<MergeListingGroupInputListFields>();
  const mergeListingGroupInputs = watch('mergeListingGroupInputs');
  const { eventMarketplaceSettings, isUKEvent, isInternationalEvent } =
    useEventMarketplaceSettings(event);

  const onListingBroadcastToggle = useCallback(
    (isSelected: boolean, isBroadcasting: boolean, mp: Marketplace) => {
      const updated = (mergeListingGroupInputs ?? []).map((input) => {
        const { listingMarketplaceSettings } = input?.marketplaceSettings || {
          listingMarketplaceSettings: {},
        };
        const marketplaceId = getMarketplaceIdFromMarketplace(mp);
        const copied = { ...listingMarketplaceSettings };
        if (isSelected) {
          Object.assign(copied, {
            [marketplaceId + '']: {
              listingBroadcastStateId: isBroadcasting ? 2 : 1,
            },
          });
        } else {
          delete copied[marketplaceId];
        }
        Object.assign(input, {
          marketplaceSettings: { listingMarketplaceSettings: copied },
        });
        return input;
      });
      setValue(`mergeListingGroupInputs`, updated);
    },
    [mergeListingGroupInputs, setValue]
  );

  const mergedSettings = useMemo(() => {
    return (mergeListingGroupInputs ?? []).reduce(
      (arr, input: MergeListingGroupInput) => {
        return mergeListingMarketplaceSettings(
          arr,
          input?.marketplaceSettings?.listingMarketplaceSettings ?? {}
        );
      },
      {} as { [key: string]: ListingMarketplaceSetting }
    );
  }, [mergeListingGroupInputs]);

  const marketplacesSelected = useMemo(() => {
    const selected: Marketplace[] = [];
    const includedIds = Object.keys(mergedSettings);
    Object.values(Marketplace).forEach((mp, index) => {
      if (includedIds.includes(index + 1 + '')) {
        selected.push(mp);
      }
    });
    return selected;
  }, [mergedSettings]);

  const marketplaceToStateId = useMemo(() => {
    const marketplaceToId = {} as Record<Marketplace, number>;
    const includedIds = Object.keys(mergedSettings);
    Object.values(Marketplace).forEach((mp, index) => {
      const key = index + 1 + '';
      if (includedIds.includes(key)) {
        marketplaceToId[mp] = mergedSettings[key].listingBroadcastStateId;
      }
    });
    return marketplaceToId;
  }, [mergedSettings]);

  return (
    <MultiDetailSection
      name={<Content id={ContentId.Broadcast} />}
      extraContent={[]}
    >
      <SimpleTable.Table>
        <SimpleTable.Thead>
          <SimpleTable.Tr>
            <SimpleTable.Th
              className={`${styles.broadcastHeaderCell} ${styles.broadcastCell}`}
            ></SimpleTable.Th>
            <SimpleTable.Th
              className={`${styles.broadcastHeaderCell} ${styles.broadcastCell}`}
            >
              <Content id={ContentId.Merchant} />
            </SimpleTable.Th>
            <SimpleTable.Th
              className={`${styles.broadcastHeaderCell} ${styles.broadcastCell}`}
            >
              <Content id={ContentId.Broadcast} />
            </SimpleTable.Th>
            <SimpleTable.Th
              className={`${styles.broadcastHeaderCell} ${styles.broadcastCell}`}
            >
              <Content id={ContentId.Status} />
            </SimpleTable.Th>
          </SimpleTable.Tr>
        </SimpleTable.Thead>
        <SimpleTable.Tbody>
          {eventMarketplaceSettings
            .toSorted((a, b) => compareMarketplace(a.mkp, b.mkp))
            .flatMap((mk) => {
              const isSelected = marketplacesSelected.includes(mk.mkp);
              const isActive = marketplaceToStateId[mk.mkp] === 2;

              if (mk.mkp === Marketplace.StubHub) {
                return [
                  <MarketplaceDisplay
                    key={`${mk}-${false}`}
                    marketplace={mk.mkp}
                    marketplaceActive={isMarketplaceActive(mk)}
                    isActive={isActive}
                    isSelected={isSelected}
                    onListingBroadcastToggle={onListingBroadcastToggle}
                    isLoading={false}
                    isUKEvent={isUKEvent}
                  />,
                  <MarketplaceDisplay
                    key={`${mk}-${true}`}
                    marketplace={mk.mkp}
                    marketplaceActive={isMarketplaceActive(mk)}
                    isViagogo
                    isActive={isActive}
                    isSelected={isSelected}
                    onListingBroadcastToggle={onListingBroadcastToggle}
                    isLoading={false}
                  />,
                ];
              }

              if (!event || !isInternationalEvent) {
                return [
                  <MarketplaceDisplay
                    key={`${mk}-${false}`}
                    marketplace={mk.mkp}
                    marketplaceActive={isMarketplaceActive(mk)}
                    isActive={isActive}
                    isSelected={isSelected}
                    onListingBroadcastToggle={onListingBroadcastToggle}
                    isLoading={false}
                  />,
                ];
              }

              // Only support Stubhub/Viagogo for international events
              return [];
            })}
        </SimpleTable.Tbody>
      </SimpleTable.Table>
    </MultiDetailSection>
  );
};
